import type React from "react";
import { lowerFirst } from "lodash";
import { FCI_ERROR } from "@searchui-searchforms/shared/fci";
import { FieldComposer, fieldToConfig } from "@/features/form-fields";
import type { Field, NameFieldGroup } from "@/entities/form-metadata";
import { NameAssertion } from "@/entities/form-metadata";
import { ErrorBoundary } from "@/shared/components/ErrorBoundary";
import type { GridSpan } from "@/shared/components/Grid";
import { Grid } from "@/shared/components/Grid";
import { useErrorBoundaryLogger } from "@/shared/lib/fci";
import { QuickFill } from "./quick-fill";

interface NameGroupProps {
    fieldGroup: NameFieldGroup;
}

export const NameGroup: React.FC<NameGroupProps> = ({ fieldGroup }) => {
    const spanDesktop = fieldGroup.fields.length <= 2 ? 6 : 4;

    const generateNameFields = (nameItemData: Field<NameAssertion>) => {
        const {
            assertion,
            features: { gridColumnSpan },
        } = nameItemData;

        return (
            <Grid.Field
                key={assertion}
                spanDesktop={(gridColumnSpan as GridSpan) ?? spanDesktop}
                spanTablet={(gridColumnSpan as GridSpan) ?? spanDesktop}
                spanMobile={12}
            >
                <FieldComposer
                    fieldPath={`name.${lowerFirst(assertion)}`}
                    {...(assertion !== NameAssertion.SecondSurname
                        ? { exactnessFieldPath: `name.${lowerFirst(assertion)}Exactness` }
                        : {})}
                    fieldConfig={fieldToConfig(nameItemData)}
                >
                    <FieldComposer.Label />
                    <FieldComposer.FieldConnector>
                        {({ formField, fieldPath, fieldConfig }, fieldHtmlId) => (
                            <FieldComposer.Inputs.String
                                id={fieldHtmlId}
                                data-testid={fieldPath}
                                required={fieldConfig.required}
                                {...formField}
                            />
                        )}
                    </FieldComposer.FieldConnector>
                    {assertion !== NameAssertion.SecondSurname && <FieldComposer.NameExactness />}
                </FieldComposer>
            </Grid.Field>
        );
    };

    const logger = useErrorBoundaryLogger();

    const showQuickFill = fieldGroup.features?.quickFill ?? false;

    return (
        <>
            <Grid.Container>{fieldGroup.fields.map(generateNameFields)}</Grid.Container>
            {showQuickFill && (
                <ErrorBoundary fallback={null} onError={logger(FCI_ERROR.Supplemental)}>
                    <QuickFill />
                </ErrorBoundary>
            )}
        </>
    );
};
